import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputAdornment,
  IconButton,
  Avatar,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  GraduationCap,
  User,
  Lock,
  Eye,
  EyeOff,
  KeyRound,
  ArrowLeft,
  Mail,
  LogIn,
} from 'lucide-react';
import api from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { GlassCard } from '../components/GlassCard';
import { APP_VERSION } from '../version';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccessMsg, setResetSuccessMsg] = useState('');
  const [resetErrorMsg, setResetErrorMsg] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isSessionExpired = searchParams.get('reason') === 'expired';
  const redirectPath = searchParams.get('redirect');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loginLoading) return;
    setError('');
    setLoginLoading(true);

    try {
      const response = await api.post('/auth/login', {
        username: username.trim(),
        password,
      });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (redirectPath && redirectPath.startsWith('/dashboard')) {
        navigate(redirectPath);
      } else if (response.data.user?.role === 'RESEARCH_STAFF') {
        navigate('/dashboard/research');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'ชื่อผู้ใช้งานหรือรหัสผ่านไม่ถูกต้อง');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const res = await api.post('/auth/google-login', {
        credential: credentialResponse.credential,
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));

      if (res.data.isNew) {
        navigate('/profile-setup');
      } else if (redirectPath && redirectPath.startsWith('/dashboard')) {
        navigate(redirectPath);
      } else if (res.data.user?.role === 'RESEARCH_STAFF') {
        navigate('/dashboard/research');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('การเข้าสู่ระบบด้วย Google ไม่สำเร็จ');
    }
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail || !resetEmail.trim()) {
      setResetErrorMsg('กรุณากรอกอีเมลที่ลงทะเบียนไว้');
      return;
    }

    setResetLoading(true);
    setResetErrorMsg('');
    setResetSuccessMsg('');

    try {
      const res = await api.post('/auth/forgot-password', { email: resetEmail.trim() });
      setResetSuccessMsg(res.data.message || 'ส่งลิงก์รีเซ็ตรหัสผ่านไปยังอีเมลของคุณเรียบร้อยแล้ว');
    } catch (err: any) {
      setResetErrorMsg(err.response?.data?.message || 'เกิดข้อผิดพลาดในการส่งคำขอรีเซ็ตรหัสผ่าน');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseReset = () => {
    setOpenReset(false);
    setResetEmail('');
    setResetSuccessMsg('');
    setResetErrorMsg('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 5,
        background:
          'radial-gradient(circle at 50% 25%, rgba(30, 64, 175, 0.08) 0%, rgba(248, 250, 252, 0) 70%)',
      }}
    >
      <Container maxWidth="sm">
        <GlassCard
          sx={{
            p: { xs: 3, sm: 5 },
            border: '1px solid rgba(255, 255, 255, 0.85)',
            boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
          }}
        >
          {/* Brand Header (PTLE Login Style) */}
          <Box sx={{ textAlign: 'center', mb: 3.5 }}>
            <Avatar
              src="/logo.png"
              alt="TPMS Logo"
              sx={{
                width: 60,
                height: 60,
                mx: 'auto',
                mb: 1.5,
                p: 0.5,
                bgcolor: 'primary.main',
                boxShadow: '0 8px 16px rgba(30, 64, 175, 0.25)',
              }}
            >
              <GraduationCap size={30} />
            </Avatar>
            <Typography variant="h4" component="h1" color="primary.main" sx={{ fontWeight: 700 }}>
              เข้าสู่ระบบ TPMS
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              ระบบบริหารจัดการงานฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร (KPRU)
            </Typography>
          </Box>

          {isSessionExpired && !error && (
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              เซสชันการใช้งานหมดอายุเนื่องจากไม่มีกิจกรรมเป็นเวลานาน กรุณาเข้าสู่ระบบใหม่อีกครั้ง
            </Alert>
          )}

          {redirectPath === '/dashboard/research' && !error && !isSessionExpired && (
            <Alert severity="info" sx={{ mb: 2.5 }}>
              กรุณาเข้าสู่ระบบก่อน เพื่อเข้าถึงการสืบค้นฐานข้อมูลงานวิจัยในชั้นเรียน
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin}>
            <TextField
              margin="normal"
              required
              fullWidth
              disabled={loginLoading}
              label="ชื่อผู้ใช้ หรือ รหัสนักศึกษา (Username)"
              placeholder="เช่น รหัสนักศึกษา หรือชื่อผู้ใช้"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <User size={18} color="#64748B" />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              disabled={loginLoading}
              label="รหัสผ่าน (Password)"
              placeholder="ระบุรหัสผ่านของคุณ"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={18} color="#64748B" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        aria-label="สลับการแสดงรหัสผ่าน"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, mb: 2 }}>
              <Button
                color="primary"
                size="small"
                onClick={() => setOpenReset(true)}
                sx={{ fontWeight: 600, px: 1 }}
              >
                ลืมรหัสผ่าน? (Forgot Password)
              </Button>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loginLoading}
                startIcon={loginLoading ? <CircularProgress size={20} color="inherit" /> : <LogIn size={19} />}
                sx={{ py: 1.4, fontWeight: 600, flex: 1 }}
              >
                {loginLoading ? 'กำลังตรวจสอบข้อมูล...' : 'เข้าสู่ระบบ (Sign In)'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                disabled={loginLoading}
                startIcon={<ArrowLeft size={18} />}
                onClick={() => navigate('/')}
                sx={{
                  py: 1.4,
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 },
                }}
              >
                หน้าแรกเว็บไซต์
              </Button>
            </Stack>

            <Divider sx={{ my: 2.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                หรือเข้าสู่ระบบด้วยบัญชี Google
              </Typography>
            </Divider>

            <Box
              sx={{
                p: 2,
                border: '1px solid #E2E8F0',
                borderRadius: 3,
                bgcolor: 'rgba(241, 245, 249, 0.75)',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                color="primary.main"
                sx={{ fontWeight: 700, mb: 1, display: 'block' }}
              >
                เฉพาะอาจารย์นิเทศก์ และครูพี่เลี้ยง
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google Login Failed')}
                  useOneTap
                  theme="filled_blue"
                  shape="pill"
                />
              </Box>
            </Box>
          </Box>
        </GlassCard>

        <Box sx={{ mt: 3.5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            ผู้พัฒนาระบบ วิวัฒน์ ทวีทรัพย์ ({APP_VERSION})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
            ฝ่ายฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
          </Typography>
        </Box>
      </Container>

      {/* Forgot Password Dialog */}
      <Dialog open={openReset} onClose={resetLoading ? undefined : handleCloseReset} fullWidth maxWidth="xs">
        <form onSubmit={handleRequestReset}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
            <KeyRound size={20} />
            <span>ลืมรหัสผ่าน (Forgot Password)</span>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              กรุณากรอกอีเมลที่ท่านลงทะเบียนไว้ในระบบ ระบบจะส่งลิงก์สำหรับตั้งรหัสผ่านใหม่ไปยังอีเมลของท่าน
            </Typography>

            {resetSuccessMsg && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {resetSuccessMsg}
              </Alert>
            )}

            {resetErrorMsg && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {resetErrorMsg}
              </Alert>
            )}

            {!resetSuccessMsg && (
              <TextField
                autoFocus
                required
                fullWidth
                label="อีเมลที่ลงทะเบียน (Email)"
                type="email"
                placeholder="example@kpru.ac.th"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={resetLoading}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={18} color="#64748B" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ mt: 1 }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseReset} color="inherit" disabled={resetLoading}>
              {resetSuccessMsg ? 'ปิดหน้าต่าง' : 'ยกเลิก'}
            </Button>
            {!resetSuccessMsg && (
              <Button
                type="submit"
                variant="contained"
                disabled={resetLoading || !resetEmail.trim()}
              >
                {resetLoading ? 'กำลังส่งอีเมล...' : 'ส่งลิงก์รีเซ็ตรหัสผ่าน'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Login;
